import React, { useState } from "react";
import { motion } from "framer-motion";
import { Wallet, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { money } from "@/lib/dateHelpers";

export default function PaymentModal({ client, onClose, onSubmit }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid payment amount");
      return;
    }
    await onSubmit(amt, note);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl"
        data-testid="payment-modal"
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet size={22} className="text-slate-700" />
            <h2 className="text-2xl font-bold">Record Payment</h2>
          </div>
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-2xl"
            data-testid="close-payment-btn"
          >
            <X size={18} />
          </Button>
        </div>
        <p className="mb-4 text-sm text-slate-600">
          For <span className="font-semibold text-slate-900">{client.dogName}</span> (
          {client.ownerName}) — current balance{" "}
          <span className="font-semibold">{money(client.balance)}</span>
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block space-y-1 text-sm font-medium">
            Payment Amount (USD)
            <input
              data-testid="payment-amount-input"
              type="number"
              min="0"
              step="0.01"
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 150.00"
              className="w-full rounded-xl border p-3 text-lg font-normal"
            />
          </label>
          <label className="block space-y-1 text-sm font-medium">
            Note (optional)
            <input
              data-testid="payment-note-input"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Cash for 3-session pack"
              className="w-full rounded-xl border p-3 font-normal"
            />
          </label>
          <div className="rounded-2xl bg-slate-50 p-4 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">New balance</span>
              <span className="font-semibold">
                {money(Math.max(0, Number(client.balance || 0) - Number(amount || 0)))}
              </span>
            </div>
            <div className="mt-1 flex justify-between">
              <span className="text-slate-600">New total paid</span>
              <span className="font-semibold">
                {money(Number(client.totalPaid || 0) + Number(amount || 0))}
              </span>
            </div>
          </div>
          <div className="flex gap-3">
            <Button data-testid="confirm-payment-btn" type="submit" className="flex-1 rounded-2xl py-5">
              Record Payment
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-2xl py-5"
            >
              Cancel
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
